import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlayerPillarTrend } from '../../api';
import { usePlayerData } from './PlayerDataContext';
import { C, FONT_UI, FONT_MONO } from './dayspineTokens';

// "Where you stand" add-on card (design handoff ADDON_where_you_stand_1) — sits between the
// task spine and the "Last 7 days" timeline on the player Today screen. Fixed identity colors
// per pillar (not the timeline's target-zone scale — see that file's header comment for why
// those two color systems are deliberately kept separate).
//
// Delta captions ("+4 vs last week") are computed here from GET .../pillar-scores-trend's
// current+previous rows, per that endpoint's own comment on why it exists (added alongside
// this card, doesn't touch the older single-row endpoint other screens already use). Only
// physical/technical/availability are ever read — same objective/subjective split as every
// other player-facing pillar view in this app.

const PILLARS = [
  { key: 'physical',     label: 'Physical',  color: C.blue },
  { key: 'technical',    label: 'Technical', color: C.plum },
  { key: 'availability', label: 'Medical',   color: C.teal },
];
const PERIODS = [
  { value: 'week',      label: 'Week',       noun: 'week' },
  { value: 'season',    label: 'Season',     noun: 'season' },
  { value: 'preseason', label: 'Pre-season', noun: 'pre-season' },
];

function fmtRange(startStr, endStr) {
  if (!startStr || !endStr) return '';
  // period_start/period_end come back as full ISO timestamps (node-postgres serializes DATE
  // columns that way), not plain YYYY-MM-DD — parse directly rather than appending a second
  // time component, which produced "Invalid Date" here.
  const s = new Date(startStr), e = new Date(endStr);
  if (isNaN(s) || isNaN(e)) return '';
  const mon = e.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${s.getUTCDate()}–${e.getUTCDate()} ${mon}`;
}

function deltaCaption(curVal, prevVal, noun) {
  if (curVal == null || prevVal == null) return null;
  const diff = Math.round(curVal - prevVal);
  if (diff === 0) return `steady vs last ${noun}`;
  return `${diff > 0 ? '+' : '−'}${Math.abs(diff)} vs last ${noun}`;
}

export default function PlayerPillarStanding({ playerRecord }) {
  const navigate = useNavigate();
  const { weekNum } = usePlayerData();
  const playerId = playerRecord?.id;
  const [period, setPeriod] = useState('week');
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    getPlayerPillarTrend(playerId, period)
      .then(r => setTrend(r))
      .catch(() => setTrend(null));
  }, [playerId, period]);

  if (!playerId) return null;

  const cur = trend?.current || null;
  const prev = trend?.previous || null;
  const periodMeta = PERIODS.find(p => p.value === period);
  const n = v => (v === null || v === undefined ? null : Number(v));

  const scopeNote = !cur
    ? 'no data yet'
    : period === 'week'
      ? `Week ${weekNum}${fmtRange(cur.period_start, cur.period_end) ? ' · ' + fmtRange(cur.period_start, cur.period_end) : ''}`
      : `${periodMeta.label}${fmtRange(cur.period_start, cur.period_end) ? ' · ' + fmtRange(cur.period_start, cur.period_end) : ''}`;

  return (
    <div style={{ borderRadius: 28, background: C.surface, padding: '20px 20px 18px', boxShadow: '0 2px 16px rgba(27,37,64,.05)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ font: '700 15px ' + FONT_UI, letterSpacing: '-.2px', color: C.ink }}>Where you stand</div>
        <div style={{ font: '400 11px ' + FONT_UI, color: C.ink45 }}>{scopeNote}</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginTop: 17 }}>
        {PILLARS.map(p => {
          const val = n(cur?.[p.key]);
          const prevVal = n(prev?.[p.key]);
          const delta = deltaCaption(val, prevVal, periodMeta.noun);
          const tappable = p.key === 'technical';
          return (
            <div key={p.key}
              onClick={tappable ? () => navigate('/player/me/technical') : undefined}
              style={{ cursor: tappable ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ font: '600 12.5px ' + FONT_UI, color: 'rgba(27,37,64,.75)' }}>{p.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <div style={{ font: '700 17px ' + FONT_UI, color: p.color }}>{val == null ? '—' : Math.round(val)}</div>
                  {delta && <div style={{ font: '500 10px ' + FONT_MONO, color: C.ink42, whiteSpace: 'nowrap' }}>{delta}</div>}
                </div>
              </div>
              <div style={{ height: 9, borderRadius: 6, background: 'rgba(27,37,64,.07)', overflow: 'hidden', marginTop: 8 }}>
                <div style={{
                  height: '100%', borderRadius: 6, background: p.color,
                  width: `${val == null ? 0 : Math.max(0, Math.min(100, val))}%`,
                  transition: 'width .6s cubic-bezier(.2,.8,.2,1)',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 5, marginTop: 19, padding: 4, borderRadius: 14, background: '#f2f4fa' }}>
        {PERIODS.map(p => {
          const active = period === p.value;
          return (
            <div key={p.value} onClick={() => setPeriod(p.value)} style={{
              flex: 1, textAlign: 'center', padding: '9px 4px', borderRadius: 11, cursor: 'pointer',
              font: '600 11.5px ' + FONT_UI, whiteSpace: 'nowrap', transition: 'all .22s ease',
              background: active ? '#ffffff' : 'transparent',
              color: active ? C.ink : 'rgba(27,37,64,.5)',
              border: `1px solid ${active ? 'rgba(27,37,64,.14)' : 'transparent'}`,
            }}>
              {p.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}
